import { Phone, ArrowRight, TrendingUp, Target, Sparkles } from "lucide-react";
import { BUSINESS, IMAGES } from "@/lib/constants";

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden bg-white"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-50 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-orange-50 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        {/* Left: Copy */}
        <div className="lg:col-span-6 animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-orange/10 text-orange px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-[0.15em] mb-6" data-testid="hero-badge">
            <Sparkles size={14} />
            <span>Pune's Real Estate Marketing Specialists</span>
          </div>

          <h1 className="font-extrabold text-navy leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            Real Estate <span className="text-orange">Digital Marketing</span> Agency in Pune
          </h1>

          <p className="mt-6 text-lg sm:text-xl font-semibold text-blue">
            Generate High-Quality Real Estate Leads That Convert
          </p>

          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl">
            Helping Developers, Builders, and Channel Partners generate qualified leads
            through Google Ads, Meta Ads, Landing Pages, and Performance Marketing.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#contact"
              data-testid="hero-book-consultation-btn"
              className="inline-flex items-center gap-2 bg-orange hover:bg-[var(--orange-dark)] text-white font-semibold px-6 py-3.5 rounded-full transition-all btn-shine shadow-lg shadow-orange/20 hover:shadow-xl hover:shadow-orange/30"
            >
              Book Free Consultation
              <ArrowRight size={18} />
            </a>
            <a
              href={`tel:${BUSINESS.phone}`}
              data-testid="hero-call-now-btn"
              className="inline-flex items-center gap-2 bg-navy hover:bg-slate-800 text-white font-semibold px-6 py-3.5 rounded-full transition-all shadow-md"
            >
              <Phone size={18} />
              Call Now
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            <div data-testid="hero-stat-leads">
              <div className="text-2xl sm:text-3xl font-extrabold text-navy">10K+</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Leads Generated</div>
            </div>
            <div data-testid="hero-stat-builders">
              <div className="text-2xl sm:text-3xl font-extrabold text-navy">50+</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Builders Served</div>
            </div>
            <div data-testid="hero-stat-roi">
              <div className="text-2xl sm:text-3xl font-extrabold text-navy">3.5x</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Avg. ROI</div>
            </div>
          </div>
        </div>

        {/* Right: Image collage */}
        <div className="lg:col-span-6 relative animate-fade-in">
          <div className="relative grid grid-cols-12 grid-rows-6 gap-3 h-[420px] sm:h-[520px]">
            {/* Main tall tower image */}
            <div className="col-span-7 row-span-6 rounded-3xl overflow-hidden shadow-2xl relative">
              <img
                src={IMAGES.heroTowers}
                alt="Premium residential towers Pune real estate marketing"
                loading="eager"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
            </div>
            {/* Top right balcony */}
            <div className="col-span-5 row-span-3 rounded-3xl overflow-hidden shadow-xl relative">
              <img
                src={IMAGES.heroBalcony}
                alt="Luxury apartment balcony sunset view"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Bottom right dashboard card */}
            <div className="col-span-5 row-span-3 rounded-3xl bg-navy text-white p-5 shadow-xl flex flex-col justify-between relative overflow-hidden">
              <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-orange/20 rounded-full blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2 text-orange text-xs font-semibold uppercase tracking-wider">
                  <TrendingUp size={14} />
                  Live Campaign
                </div>
                <div className="mt-2 text-3xl font-extrabold">+247%</div>
                <div className="text-xs text-slate-300 mt-1">Lead conversion lift</div>
              </div>
              <div className="relative space-y-1.5">
                <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-400">
                  <span>CPL</span><span>↓ 38%</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full w-[72%] bg-gradient-to-r from-orange to-blue rounded-full" />
                </div>
                <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-400 pt-1">
                  <span>Qualified</span><span>92%</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full w-[92%] bg-gradient-to-r from-blue to-orange rounded-full" />
                </div>
              </div>
            </div>
          </div>

          {/* Floating accent card */}
          <div className="hidden sm:flex absolute -bottom-6 -left-4 lg:-left-8 bg-white rounded-2xl shadow-2xl p-4 items-center gap-3 border border-slate-100 animate-float">
            <div className="w-11 h-11 rounded-xl bg-orange/10 flex items-center justify-center text-orange">
              <Target size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Qualified Leads</div>
              <div className="font-bold text-navy text-sm">Daily Delivery</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
